import React, { useEffect, useState, ChangeEvent } from 'react';
import { useDispatch, connect } from 'react-redux';
import { collection, getDocs, getDoc, doc, updateDoc } from 'firebase/firestore';
import isEqual from 'lodash/isEqual';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import SaveIcon from '@mui/icons-material/Save';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DbCollections from '../../resources/enums/DbCollections';
import { AppState } from '../../store/rootReducer';
import SnackbarMessage from '../../components/SnackbarMessage/SnackbarMessage';
import Sidebar from '../../components/Sidebar/Sidebar';
import { EnhancedTableHead, EnhancedTableToolbar } from '../../components/P4STable/P4STable';
import { knowledgeInterface } from '../../containers/KnowledgePage/KnowledgePageInterfaces';
import SelectControl from '../../components/SelectControl/SelectControl';
import { ProductType } from './ProductPageInterfaces';
import { ProductsProps, StateProduct } from './ProductPageInterfaces';
import { requestGetMissionsList } from '../../store/missions/actions';
import { db } from '../../config/firebase';
import { getMissionsList } from '../../store/missions/selectors';
import strings from '../../resources/strings';
import {
    getTableColumns,
    getSelectOptions,
    mapMissionsToProducts,
    trimForInteger,
    mapExternalToProducts,
    updateResources,
} from './utils';
import { tableSettings } from './consts';

const ProductsPage: React.FC<ProductsProps> = (props) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(tableSettings.rowsPerPage);
    const [productType, setProductType] = useState<ProductType>();
    const [error, setError] = useState(false);
    const [priceError, setPriceError] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [videosContent, setVideosContent] = useState<knowledgeInterface[]>([]);
    const [articlesContent, setArticlesContent] = useState<knowledgeInterface[]>([]);
    const [tableData, setTableData] = useState<StateProduct[]>([]);
    const [inputMeetingsPrice, setInputMeetingsPrice] = useState(0);
    const [meetingsPrice, setMeetingsPrice] = useState('0');

    const dispatch = useDispatch();

    const externalContentRef = collection(db, DbCollections.ExternalContent);
    const paidMeetingsSettingsRef = doc(db, DbCollections.Settings, 'paid_meetings');

    const productOptions = getSelectOptions();
    const columns = getTableColumns(productType);
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tableData.length) : 0;

    const isSaveButtonDisabled = () => {
        if (productType === ProductType.Meeting) return String(inputMeetingsPrice) === meetingsPrice;
        if (productType === ProductType.Course) return isEqual(mapMissionsToProducts(props.missions), tableData);
        if (productType === ProductType.Article) return isEqual(mapExternalToProducts(articlesContent), tableData);
        if (productType === ProductType.Video) return isEqual(mapExternalToProducts(videosContent), tableData);
    };

    const getExternalContent = async () => {
        const data = await getDocs(externalContentRef);
        const events = data.docs.map(
            (news) =>
                ({
                    ...news.data(),
                    id: news.id,
                    publishFrom: news.data().publishFrom ? news.data().publishFrom.toDate() : null,
                    publishTo: news.data().publishTo ? news.data().publishTo.toDate() : null,
                } as knowledgeInterface),
        );
        const videos: knowledgeInterface[] = [];
        const articles: knowledgeInterface[] = [];
        events.forEach((event) => {
            if (event.type === 'video') videos.push(event);
            if (event.type === 'article') articles.push(event);
        });
        setVideosContent(videos);
        setArticlesContent(articles);
    };

    const getSettings = async () => {
        const data = await getDoc(paidMeetingsSettingsRef);
        setInputMeetingsPrice(data.data()?.price || 0);
    };

    const convertPrice = (price: string) => {
        const normalizedPrice = price.length > 1 ? price.replace(/^0+/, '') : price;
        return trimForInteger(normalizedPrice ? normalizedPrice : '');
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangeProductType = ({ productType }: Record<'productType', ProductType>) => {
        setProductType(productType);
    };

    const handleSetProductPaid = (event: ChangeEvent<HTMLInputElement>) => {
        setTableData((prevState) =>
            prevState.map((product) => {
                if (event.target.name === product.id)
                    return {
                        ...product,
                        paid: event.target.checked,
                        price: event.target.checked ? product.price : '0',
                    };
                return product;
            }),
        );
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeProductPrice = (event: ChangeEvent<HTMLInputElement>) => {
        const price = convertPrice(event.target.value);
        setTableData((prevState) =>
            prevState.map((product) => {
                if (event.target.name === product.id) return { ...product, price };
                return product;
            }),
        );
    };

    const handleChangeMeetingsPrice = (event: ChangeEvent<HTMLInputElement>) => {
        const price = convertPrice(event.target.value);
        setMeetingsPrice(price);
    };

    const handleSave = () => {
        setTableData((prevState) =>
            prevState.map((state) => {
                if (!state.price && state.paid) return { ...state, price: '0' };
                return state;
            }),
        );

        // Update meetings
        if (productType === ProductType.Meeting) {
            if (!meetingsPrice.length) {
                return setPriceError(true);
            }
            updateDoc(paidMeetingsSettingsRef, { price: Number(meetingsPrice) })
                .then(() => {
                    getSettings();
                    setSuccessMessage(strings.productsPage.saveSuccessMessage);
                })
                .catch(() => setError(true));
        }

        // Update courses
        if (productType === ProductType.Course) {
            const coursesSolvers = updateResources(tableData, DbCollections.Missions);
            Promise.all(coursesSolvers)
                .then(() => {
                    dispatch(requestGetMissionsList());
                    setSuccessMessage(strings.productsPage.saveSuccessMessage);
                })
                .catch(() => setError(true));
        }

        // Update article / video
        if (productType === ProductType.Article || productType === ProductType.Video) {
            const coursesSolvers = updateResources(tableData, DbCollections.ExternalContent);
            Promise.all(coursesSolvers)
                .then(() => {
                    getExternalContent();
                    setSuccessMessage(strings.productsPage.saveSuccessMessage);
                })
                .catch(() => setError(true));
        }
    };

    useEffect(() => {
        getExternalContent();
        getSettings();
        dispatch(requestGetMissionsList());
    }, []);

    useEffect(() => {
        if (productType === ProductType.Meeting) {
            setMeetingsPrice(String(inputMeetingsPrice));
        }
        if (productType === ProductType.Video) {
            setTableData(mapExternalToProducts(videosContent));
        }
        if (productType === ProductType.Article) {
            setTableData(mapExternalToProducts(articlesContent));
        }
        if (productType === ProductType.Course) {
            setTableData(mapMissionsToProducts(props.missions));
        }
        setSuccessMessage('');
        setSearchQuery('');
    }, [productType]);

    return (
        <Box>
            <Sidebar />
            <Box className="table-container">
                <Box sx={{ width: '100%' }}>
                    <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
                        {/* Types selector */}
                        <Box sx={{ mx: 4, mb: 4 }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 'bold', mb: 2 }} component="h3">
                                {strings.productsPage.productsTitle}
                            </Typography>
                            <SelectControl
                                singleChoice
                                componentData={productType}
                                default={''}
                                fieldName="productType"
                                label={strings.productsPage.productSelectLabel}
                                minWidth={200}
                                selectData={productOptions}
                                setComponentData={handleChangeProductType}
                            />
                        </Box>

                        {productType === ProductType.Meeting && (
                            <Box sx={{ px: 4, pt: 4 }}>
                                <Typography sx={{ fontSize: 14, mb: 2 }} component="h3">
                                    {strings.productsPage.table.costOfMeeting}
                                </Typography>
                                <TextField
                                    value={meetingsPrice}
                                    variant="outlined"
                                    type="text"
                                    label={strings.productsPage.table.price}
                                    onChange={handleChangeMeetingsPrice}
                                />
                            </Box>
                        )}

                        {productType && productType !== ProductType.Meeting && (
                            <>
                                <EnhancedTableToolbar
                                    label={strings.productsPage.searchLabelByTitle}
                                    handleSearch={setSearchQuery}
                                />
                                <TableContainer sx={{ maxHeight: '54vh' }}>
                                    <Table
                                        stickyHeader
                                        aria-labelledby="tableTitle"
                                        size="medium"
                                        sx={{ minWidth: 750 }}
                                    >
                                        <EnhancedTableHead columns={columns} />
                                        <TableBody>
                                            {tableData
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .filter((product) => {
                                                    if (!searchQuery || product.title.includes(searchQuery))
                                                        return product;
                                                })
                                                .map((product) => (
                                                    <TableRow key={product.id}>
                                                        <TableCell>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={product?.paid ?? false}
                                                                        name={product.id}
                                                                        sx={{
                                                                            '& .MuiSvgIcon-root': {
                                                                                fontSize: '2.4rem',
                                                                            },
                                                                        }}
                                                                        onChange={handleSetProductPaid}
                                                                    />
                                                                }
                                                                label={
                                                                    product.paid
                                                                        ? strings.productsPage.table.paid
                                                                        : strings.productsPage.table.free
                                                                }
                                                                sx={{
                                                                    '.MuiFormControlLabel-label': {
                                                                        fontWeight: 'bold',
                                                                        fontSize: '1.2rem',
                                                                    },
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{product.title}</TableCell>
                                                        <TableCell align="right">
                                                            <Box
                                                                sx={{
                                                                    alignItems: 'center',
                                                                    justifyContent: 'flex-end',
                                                                    display: 'flex',
                                                                }}
                                                            >
                                                                <TextField
                                                                    disabled={!product?.paid}
                                                                    value={product?.paid ? product?.price : ''}
                                                                    variant="outlined"
                                                                    type="text"
                                                                    label="Price"
                                                                    name={product.id}
                                                                    onChange={handleChangeProductPrice}
                                                                />
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            {emptyRows > 0 && (
                                                <TableRow
                                                    style={{
                                                        height: 53 * emptyRows,
                                                    }}
                                                >
                                                    <TableCell colSpan={6} />
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Pagination */}
                                <TablePagination
                                    component="div"
                                    count={tableData.length}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    rowsPerPageOptions={tableSettings.pageRows}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </>
                        )}

                        {/* Save button */}
                        {productType && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '1rem',
                                    paddingRight: '2rem',
                                    marginTop: '2rem',
                                }}
                            >
                                <Button
                                    disabled={isSaveButtonDisabled()}
                                    startIcon={<SaveIcon />}
                                    variant="outlined"
                                    sx={{ backgroundColor: '#E3F2FD', color: '#2196F3' }}
                                    onClick={handleSave}
                                >
                                    {strings.newsAndEvents.buttons.save}
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Box>

            {/* Success message */}
            {Boolean(successMessage.length) && (
                <SnackbarMessage
                    serverity="success"
                    message={successMessage}
                    clearMessage={() => {
                        setSuccessMessage('');
                    }}
                />
            )}

            {/* Error message */}
            {error && (
                <SnackbarMessage
                    serverity="error"
                    message={error ? strings.productsPage.saveErrorMessage : ''}
                    clearMessage={() => setError(false)}
                />
            )}
            {priceError && (
                <SnackbarMessage
                    serverity="error"
                    message={priceError ? strings.productsPage.priceError : ''}
                    clearMessage={() => setPriceError(false)}
                />
            )}
        </Box>
    );
};

function mapStateToProps(state: AppState) {
    return {
        missions: getMissionsList(state),
    };
}

export default connect(mapStateToProps)(ProductsPage);
